// RECALL-RULES: standing rules — what every agent (or one DE) must always or never do. One shared
// set of parts: the Recall state badge, the rule row (hover lift icon for owners and admins), the
// add modal (Setup › Recall, a DE's page, a chat message's "Make this a rule") and the lift modal.
// An owner's or admin's rule binds at once; anyone else's is a proposal decided in the Inbox
// (`memory_rule` approval), shown as "Waiting for approval". A DE's board automations are listed
// as links to Work › Automations, never copied.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  Textarea,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { queryOptions, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BanIcon, PlusIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { digitalEmployeesQuery } from "../queries";
import { problemText } from "../settings/problem-text";

export type MemoryRuleList = OperationOutput<"memoryRules.list">;
export type MemoryRule = MemoryRuleList["data"][number];
export type RecallPushState = MemoryRule["recall_state"];

export const ruleKeys = {
  all: (co: string) => ["company", co, "memory", "rules"] as const,
  list: (co: string, deId?: string) =>
    ["company", co, "memory", "rules", deId ?? "company"] as const,
};

export const memoryRulesQuery = (api: Api, co: string, deId?: string) =>
  queryOptions({
    queryKey: ruleKeys.list(co, deId),
    queryFn: () =>
      api.client.memoryRules.list({ params: { co }, query: deId ? { de_id: deId } : {} }),
  });

const RECALL_LABEL: Record<RecallPushState, string> = {
  synced: "In Recall",
  pending: "Sending to Recall",
  refused: "Recall refused",
  not_offered: "Not in Recall yet",
};

const RECALL_INFO: Record<RecallPushState, string> = {
  synced: "Recall holds it too.",
  pending: "Sent to Recall within a minute.",
  refused: "Recall said no; CompanyOS tries again later.",
  not_offered: "This Recall has no door CompanyOS may use for it yet.",
};

/** How far a record reached Recall: a small muted badge, the detail behind its (i). */
export function RecallStateBadge({
  state,
  detail,
  note,
}: {
  state: RecallPushState;
  detail: string | null;
  /** A line under the detail (what holds either way). */
  note?: string;
}) {
  return (
    <span className="inline-flex items-center gap-0.5" data-slot="recall-state" data-state={state}>
      <Badge variant={state === "refused" ? "warning" : "outline"} className="h-5 text-[11px]">
        {RECALL_LABEL[state]}
      </Badge>
      <InfoTip label={`About ${RECALL_LABEL[state].toLowerCase()}`}>
        {detail ?? RECALL_INFO[state]}
        {note ? <span className="mt-1 block">{note}</span> : null}
      </InfoTip>
    </span>
  );
}

const SOURCE: Record<MemoryRule["source"], string> = {
  setup: "Setup",
  de_page: "DE page",
  chat: "Chat",
  de_suggestion: "Suggested by DE",
  recall_proposal: "Recall proposal",
};

const BOUND_NOTE = "Agents it binds are told it at every wake-up either way.";

/** One rule: title, text (two lines), where it came from, and its Recall state or approval. */
export function RuleRow({
  company,
  rule: r,
  canLift,
  onLift,
  showDe = false,
}: {
  company: CompanyAccess;
  rule: MemoryRule;
  canLift: boolean;
  onLift: (r: MemoryRule) => void;
  showDe?: boolean;
}) {
  return (
    <li
      className="group/rule flex min-w-0 items-start gap-2 py-1.5"
      data-slot="memory-rule"
      data-rule={r.id}
      data-status={r.status}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-foreground">
          <span className="truncate">{r.title}</span>
          {showDe && r.de ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-normal text-muted-foreground">
              <WorkerBadge kind="de" size="sm" />
              {workerName(r.de.name)}
            </span>
          ) : null}
        </span>
        {r.text !== r.title ? (
          <p className="line-clamp-2 text-[13px] text-muted-foreground" title={r.text}>
            {r.text}
          </p>
        ) : null}
        <span className="flex flex-wrap items-center gap-1.5 text-xs text-faint">
          {SOURCE[r.source]}
          {r.status === "proposed" ? (
            r.approval_id ? (
              <Link
                to="/c/$slug/agents/approvals/$id"
                params={{ slug: company.slug, id: r.approval_id }}
                search={keepFocus}
                data-slot="rule-waiting"
                className="rounded-full focus-visible:outline-2 focus-visible:outline-ring"
              >
                <Badge variant="warning" className="h-5 text-[11px]">
                  Waiting for approval
                </Badge>
              </Link>
            ) : (
              <Badge variant="warning" className="h-5 text-[11px]">
                Waiting for approval
              </Badge>
            )
          ) : (
            <RecallStateBadge state={r.recall_state} detail={r.recall_detail} note={BOUND_NOTE} />
          )}
        </span>
      </div>
      {canLift && r.status === "active" ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Lift ${r.title}`}
          title="Lift rule"
          data-slot="lift-rule"
          className="shrink-0 opacity-0 group-hover/rule:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
          onClick={() => onLift(r)}
        >
          <BanIcon aria-hidden="true" />
        </Button>
      ) : null}
    </li>
  );
}

/** The "+" that opens the add modal (a visible icon). */
export function AddRuleButton({
  onClick,
  label = "Add rule",
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      data-slot="add-rule"
      onClick={onClick}
    >
      <PlusIcon aria-hidden="true" />
    </Button>
  );
}

/** The chosen scope reads as chosen (outline alone looks the same either way). */
const PRESSED = "border-primary bg-primary-soft text-primary-text hover:bg-primary-soft";

export type RuleDraft = {
  scope: "company" | "de";
  deId?: string;
  text?: string;
  source: "setup" | "de_page" | "chat";
  messageId?: string;
};

/** Add a standing rule: for every agent or one DE; title and why optional. */
export function RuleDialog({
  api,
  co,
  initial,
  canManage,
  onClose,
}: {
  api: Api;
  co: string;
  initial: RuleDraft;
  /** An owner or admin (binds at once); false: the rule is proposed. Unknown: undefined. */
  canManage?: boolean | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  const list = (des.data?.pages ?? []).flatMap((p) => p.data).filter((d) => d.status !== "retired");
  const [scope, setScope] = useState(initial.scope);
  const [deId, setDeId] = useState(initial.deId ?? "");
  const [title, setTitle] = useState("");
  const [text, setText] = useState(initial.text ?? "");
  const [why, setWhy] = useState("");
  const ids = { de: useId(), title: useId(), text: useId(), why: useId() };
  const create = useMutation({
    mutationFn: () =>
      api.client.memoryRules.create({
        params: { co },
        body: {
          scope,
          ...(scope === "de" ? { de_id: deId } : {}),
          ...(title.trim() ? { title: title.trim() } : {}),
          text: text.trim(),
          ...(why.trim() ? { why: why.trim() } : {}),
          source: initial.source,
          ...(initial.messageId ? { message_id: initial.messageId } : {}),
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ruleKeys.all(co) });
      toast.add({
        title: r.status === "proposed" ? "Sent for approval" : "Rule added",
        type: "success",
      });
      onClose();
    },
  });
  const ok =
    text.trim().length > 0 &&
    text.trim().length <= 1000 &&
    title.trim().length <= 120 &&
    (scope === "company" || deId !== "");
  return (
    <QuickAddDialog
      title="New rule"
      info="What agents must always or never do. Every run of the agents it binds is told it at wake-up. An owner's or admin's rule binds at once; anyone else's waits for approval."
      description={canManage === false ? "Waits for an owner's approval." : undefined}
      submitLabel="Add rule"
      pending={create.isPending}
      canSubmit={ok}
      onSubmit={() => create.mutate()}
      onClose={onClose}
      slot="rule-dialog"
      error={
        create.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(create.error, "The rule was not added.")}
          </p>
        ) : null
      }
    >
      <fieldset className="flex flex-wrap items-center gap-1.5" data-slot="rule-scope">
        <legend className="sr-only">Applies to</legend>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={scope === "company" ? PRESSED : undefined}
          aria-pressed={scope === "company"}
          onClick={() => setScope("company")}
        >
          Every agent
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={scope === "de" ? PRESSED : undefined}
          aria-pressed={scope === "de"}
          onClick={() => setScope("de")}
        >
          One DE
        </Button>
      </fieldset>
      {scope === "de" ? (
        <Field>
          <FieldLabel htmlFor={ids.de}>DE</FieldLabel>
          <NativeSelect id={ids.de} value={deId} onChange={(e) => setDeId(e.target.value)}>
            <NativeSelectOption value="">Choose a DE</NativeSelectOption>
            {list.map((d) => (
              <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                {`${workerName(d.display_name)} (DE)`}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
      ) : null}
      <Field>
        <FieldLabel htmlFor={ids.text}>Rule</FieldLabel>
        <Textarea
          id={ids.text}
          rows={3}
          maxLength={1000}
          placeholder="Never email a candidate before 8:00 their time."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={ids.title}>Title (optional)</FieldLabel>
        <Input
          id={ids.title}
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={ids.why}>Why (optional)</FieldLabel>
        <Input id={ids.why} maxLength={1000} value={why} onChange={(e) => setWhy(e.target.value)} />
      </Field>
    </QuickAddDialog>
  );
}

/** Lift an active rule (owners and admins): kept as history, lifted in Recall. */
export function LiftRuleDialog({
  api,
  co,
  rule,
  onClose,
}: {
  api: Api;
  co: string;
  rule: MemoryRule;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const id = useId();
  const lift = useMutation({
    mutationFn: () =>
      api.client.memoryRules.lift({
        params: { co, id: rule.id },
        body: reason.trim() ? { reason: reason.trim() } : {},
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ruleKeys.all(co) });
      toast.add({ title: "Rule lifted", type: "success" });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Lift rule"
      info="Agents stop being told it from their next wake-up. It stays in the history."
      description={rule.title}
      submitLabel="Lift rule"
      pending={lift.isPending}
      onSubmit={() => lift.mutate()}
      onClose={onClose}
      slot="lift-rule-dialog"
      error={
        lift.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(lift.error, "The rule was not lifted.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={id}>Reason (optional)</FieldLabel>
        <Input id={id} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </QuickAddDialog>
  );
}

/** The add and lift modals a list opens, as state. */
export function useRuleModals(api: Api, co: string, canManage: boolean | undefined) {
  const [adding, setAdding] = useState<RuleDraft | null>(null);
  const [lifting, setLifting] = useState<MemoryRule | null>(null);
  const dialogs = (
    <>
      {adding ? (
        <RuleDialog
          api={api}
          co={co}
          initial={adding}
          canManage={canManage}
          onClose={() => setAdding(null)}
        />
      ) : null}
      {lifting ? (
        <LiftRuleDialog api={api} co={co} rule={lifting} onClose={() => setLifting(null)} />
      ) : null}
    </>
  );
  return { add: setAdding, lift: setLifting, dialogs };
}
